import React, { useState, useEffect } from 'react';
import { getVideoDetails } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import './VideoDetailsModal.css';

export default function VideoDetailsModal({ videoId, onClose }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const data = await getVideoDetails(videoId);
        setDetails(data);
      } catch (err) {
        console.error('Error fetching video details:', err);
        setError(t.videoDetails?.failedToLoad || 'Failed to load video details');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [videoId, t.videoDetails?.failedToLoad]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert(t.videoDetails?.copiedToClipboard || 'Copied to clipboard!');
  };

  const getLanguageDisplay = (lang) => {
    if (lang === 'fi') return `🇫🇮 ${t.prompt?.finnish || 'Finnish'}`;
    if (lang === 'sv') return `🇸🇪 ${t.prompt?.swedish || 'Swedish'}`;
    if (lang === 'en') return `🇬🇧 ${t.prompt?.english || 'English'}`;
    return lang;
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      pending: t.videoDetails?.pending || 'Pending',
      processing: t.videoDetails?.processing || 'Processing',
      completed: t.videoDetails?.completed || 'Completed',
      failed: t.videoDetails?.failed || 'Failed'
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>{t.videoDetails?.loadingDetails || 'Loading details...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">
            <p>{error || t.videoDetails?.videoNotFound || 'Video not found'}</p>
            <button onClick={onClose}>{t.close || 'Close'}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content video-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>🎹 {details.scene_name}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="detail-tabs">
          <button 
            className={`detail-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            ℹ️ {t.videoDetails?.info || 'Info'}
          </button>
          <button 
            className={`detail-tab ${activeTab === 'prompt' ? 'active' : ''}`}
            onClick={() => setActiveTab('prompt')}
          >
            💭 {t.videoDetails?.promptTab || 'Prompt'}
          </button>
          <button 
            className={`detail-tab ${activeTab === 'code' ? 'active' : ''}`}
            onClick={() => setActiveTab('code')}
          >
            💻 {t.videoDetails?.code || 'Code'}
          </button>
        </div>

        {/* Tab Content */}
        <div className="detail-content">
          
          {/* INFO TAB */}
          {activeTab === 'info' && (
            <div className="info-grid">
              <div className="info-item">
                <label>{t.videoDetails?.sceneName || 'Scene Name'}:</label>
                <span>{details.scene_name}</span>
              </div>
              
              <div className="info-item">
                <label>{t.videoDetails?.status || 'Status'}:</label>
                <span className={`status-badge ${details.status}`}>
                  {getStatusDisplay(details.status)}
                </span>
              </div>
              
              <div className="info-item">
                <label>{t.videoDetails?.quality || 'Quality'}:</label>
                <span>{details.quality?.toUpperCase()}</span>
              </div>
              
              <div className="info-item">
                <label>{t.videoDetails?.language || 'Language'}:</label>
                <span>{getLanguageDisplay(details.language)}</span>
              </div>
              
              <div className="info-item">
                <label>{t.videoDetails?.created || 'Created'}:</label>
                <span>{new Date(details.created_at).toLocaleString()}</span>
              </div>
              
              {details.file_size && (
                <div className="info-item">
                  <label>{t.videoDetails?.fileSize || 'File Size'}:</label>
                  <span>{(details.file_size / (1024 * 1024)).toFixed(2)} MB</span>
                </div>
              )}
              
              {details.duration && (
                <div className="info-item">
                  <label>{t.videoDetails?.duration || 'Duration'}:</label>
                  <span>{details.duration}s</span>
                </div>
              )}
              
              {details.error_message && (
                <div className="info-item full-width">
                  <label>{t.videoDetails?.errorMessage || 'Error'}:</label>
                  <span className="error-text">{details.error_message}</span>
                </div>
              )}
            </div>
          )}

          {/* PROMPT TAB */}
          {activeTab === 'prompt' && (
            <div className="prompt-section">
              <div className="section-header">
                <h3>{t.videoDetails?.originalPrompt || 'Original Prompt'}</h3>
                <button 
                  className="copy-button"
                  onClick={() => copyToClipboard(details.prompt)}
                >
                  📋 {t.copy || 'Copy'}
                </button>
              </div>
              <div className="prompt-text">
                {details.prompt}
              </div>
            </div>
          )}

          {/* CODE TAB */}
          {activeTab === 'code' && (
            <div className="code-section">
              <div className="section-header">
                <h3>{t.videoDetails?.manimCode || 'Manim Code'}</h3>
                <button 
                  className="copy-button"
                  onClick={() => copyToClipboard(details.code)}
                >
                  📋 {t.videoDetails?.copyCode || 'Copy Code'}
                </button>
              </div>
              <pre className="code-display">
                <code>{details.code}</code>
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {details.video_url && (
            <a 
              href={details.video_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              🎬 {t.videoDetails?.openVideo || 'Open Video'}
            </a>
          )}
          <button onClick={onClose} className="btn-secondary">
            {t.close || 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
